import { useCallback, useEffect, useRef, useState } from "react";
import { Folder, FolderPlus, Upload, ChevronRight, ChevronLeft, Trash2, Loader2, X, Check, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useLiveReload } from "@/lib/live";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { MediaThumb, VideoPlayer } from "@/components/Bits";
import { SaveButton, saveToPhone } from "@/components/MobileTools";
import { ActionBar, FolderPicker, RenameDialog } from "@/components/DriveTools";
import { enqueueUploads } from "@/lib/uploads";

function TextPreview({ file }) {
  const [text, setText] = useState("Lädt…");
  useEffect(() => { api.get(`/files/${file.id}/content`, { responseType: "text" }).then((r) => setText(r.data)); }, [file.id]);
  return <pre className="w-full max-w-2xl max-h-full overflow-auto whitespace-pre-wrap text-sm bg-[#33373f] border border-border rounded-xl p-5 text-slate-200 font-sans" data-testid="text-preview">{text}</pre>;
}

export default function Drive() {
  const { clientId, client } = useClient();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [folderId, setFolderId] = useState(null);
  const [data, setData] = useState({ folders: [], files: [], breadcrumbs: [] });
  const [newName, setNewName] = useState(null);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(() => api.get("/drive", { params: { client_id: clientId, folder_id: folderId || undefined } }).then((r) => setData(r.data)), [clientId, folderId]);
  useEffect(() => { load(); }, [load]);
  useLiveReload(load);
  useEffect(() => { setFolderId(null); }, [clientId]);
  const processing = data.files.some((f) => (f.kind === "video" || f.kind === "image") && !f.has_poster && !f.variants_failed);
  useEffect(() => { if (!processing) return; const t = setInterval(load, 4000); return () => clearInterval(t); }, [processing, load]);

  const [dragging, setDragging] = useState(false);
  const [pending, setPending] = useState(null);
  useEffect(() => {
    const over = (e) => { e.preventDefault(); if (isAdmin && e.dataTransfer?.types?.includes("Files")) setDragging(true); };
    const leave = (e) => { if (!e.relatedTarget) setDragging(false); };
    const drop = (e) => { e.preventDefault(); setDragging(false); if (isAdmin && e.dataTransfer?.files?.length) setPending(Array.from(e.dataTransfer.files)); };
    window.addEventListener("dragover", over); window.addEventListener("dragleave", leave); window.addEventListener("drop", drop);
    return () => { window.removeEventListener("dragover", over); window.removeEventListener("dragleave", leave); window.removeEventListener("drop", drop); };
  }, [isAdmin]);
  const folderName = data.breadcrumbs.length ? data.breadcrumbs[data.breadcrumbs.length - 1].name : "Alle Ordner";

  const createFolder = async () => {
    if (!newName?.trim()) return setNewName(null);
    try { await api.post("/folders", { client_id: clientId, parent_id: folderId, name: newName }); setNewName(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const upload = (fileList) => enqueueUploads(fileList, clientId, folderId);
  useEffect(() => { window.addEventListener("drive-changed", load); return () => window.removeEventListener("drive-changed", load); }, [load]);

  const delFile = async (id) => { if (!window.confirm("Datei wirklich löschen?")) return; await api.delete(`/files/${id}`); setPreview(null); load(); };

  const [sel, setSel] = useState([]);
  const [dialog, setDialog] = useState(null);
  useEffect(() => { setSel([]); }, [folderId, clientId]);
  const isSel = (type, id) => sel.some((s) => s.type === type && s.item.id === id);
  const toggle = (type, item) => setSel((cur) => (cur.some((s) => s.type === type && s.item.id === item.id) ? cur.filter((s) => !(s.type === type && s.item.id === item.id)) : [...cur, { type, item }]));
  const openFolder = (id) => setFolderId(id);
  const openFile = (f) => { setSel([]); setPreview(f); };
  const path = (s) => `/${s.type === "folder" ? "folders" : "files"}/${s.item.id}`;
  const runAll = async (fn, okMsg) => {
    const res = await Promise.allSettled(sel.map(fn));
    const bad = res.filter((r) => r.status === "rejected");
    if (bad.length) toast.error(errMsg(bad[0].reason)); else toast.success(okMsg);
    setSel([]); setDialog(null); load();
  };
  const moveSel = (t) => runAll((s) => api.patch(path(s), { move: true, target_id: t }), `${sel.length} verschoben`);
  const copySel = (t) => runAll((s) => api.post(`/files/${s.item.id}/copy`, { target_id: t }), `${sel.length} kopiert`);
  const renameSel = (name) => runAll((s) => api.patch(path(s), { name }), "Umbenannt");
  const deleteSel = () => {
    const folders = sel.filter((s) => s.type === "folder").length;
    if (!window.confirm(`${sel.length} ${sel.length === 1 ? "Element" : "Elemente"} löschen?${folders ? " Ordner werden inkl. Inhalt gelöscht." : ""}`)) return;
    runAll((s) => api.delete(path(s)), "Gelöscht");
  };
  const downloadSel = async () => { for (const s of sel) { try { await saveToPhone(s.item); } catch (e) { toast.error(e.message); } } };

  return (
    <div className="max-w-7xl">
      {dragging && (
        <div className="fixed inset-0 z-50 bg-teal-500/10 backdrop-blur-sm border-4 border-dashed border-teal-400 grid place-items-center pointer-events-none" data-testid="drop-overlay">
          <div className="text-center"><Upload className="h-10 w-10 mx-auto text-teal-300 mb-3" /><p className="text-xl font-semibold">Hier loslassen</p><p className="text-sm text-slate-300">Ablegen in „{folderName}“</p></div>
        </div>
      )}
      {pending && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" data-testid="upload-confirm">
          <div className="w-full max-w-md rounded-2xl border border-border bg-[#33373f] p-6">
            <p className="text-lg font-semibold mb-1">{pending.length} {pending.length === 1 ? "Datei" : "Dateien"} hochladen?</p>
            <p className="text-sm text-slate-400 mb-4">In den Ordner <span className="text-teal-300 font-medium">„{folderName}“</span></p>
            <ul className="max-h-40 overflow-auto text-sm text-slate-300 space-y-1 mb-6" data-testid="upload-confirm-list">
              {pending.map((f) => <li key={f.name + f.size} className="truncate">• {f.name}</li>)}
            </ul>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => setPending(null)} data-testid="upload-confirm-cancel">Abbrechen</Button>
              <Button onClick={() => { upload(pending); setPending(null); }} className="bg-teal-500 hover:bg-teal-400 text-slate-950 gap-2" data-testid="upload-confirm-submit"><Upload className="h-4 w-4" />Hochladen</Button>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Content-Drive</p>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight">{client?.name}</h1>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setNewName("")} className="gap-2" data-testid="new-folder-button"><FolderPlus className="h-4 w-4" />Ordner</Button>
            <Button onClick={() => inputRef.current?.click()} className="bg-teal-500 hover:bg-teal-400 text-slate-950 gap-2" data-testid="upload-asset-button">
              <Upload className="h-4 w-4" />Hochladen
            </Button>
            <input ref={inputRef} type="file" multiple accept="image/*,video/*,.txt,text/plain" className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="upload-file-input" />
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 mb-8" data-testid="drive-breadcrumbs">
        {data.breadcrumbs.length > 0 && (
          <button onClick={() => setFolderId(data.breadcrumbs.length > 1 ? data.breadcrumbs[data.breadcrumbs.length - 2].id : null)}
            className="h-10 pl-2 pr-4 rounded-full bg-white/5 border border-border flex items-center gap-1 text-sm font-medium hover:bg-white/10 active:scale-95 transition-transform" data-testid="drive-back-button">
            <ChevronLeft className="h-5 w-5" />Zurück
          </button>
        )}
        <div className="flex items-center gap-1 text-sm text-muted-foreground min-w-0 overflow-x-auto">
          <button onClick={() => setFolderId(null)} className="hover:text-foreground shrink-0" data-testid="breadcrumb-root">Alle Ordner</button>
          {data.breadcrumbs.map((b, i) => (
            <span key={b.id} className="flex items-center gap-1 shrink-0"><ChevronRight className="h-3.5 w-3.5" /><button onClick={() => setFolderId(b.id)} className={i === data.breadcrumbs.length - 1 ? "text-foreground font-semibold" : "hover:text-foreground"}>{b.name}</button></span>
          ))}
        </div>
      </div>

      {newName !== null && (
        <div className="flex gap-2 mb-6 max-w-sm">
          <Input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createFolder()} placeholder="Ordnername" data-testid="new-folder-input" />
          <Button onClick={createFolder} data-testid="create-folder-confirm">Anlegen</Button>
        </div>
      )}

      {data.folders.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-10">
          {data.folders.map((f) => (
            <div key={f.id} className={`group relative flex items-center gap-3 rounded-lg border bg-card px-4 py-3 cursor-pointer select-none transition-colors ${isSel("folder", f.id) ? "border-teal-400 bg-teal-500/15 ring-2 ring-teal-400/60" : "border-border hover:border-teal-500/50"}`}
              onClick={() => (isAdmin ? toggle("folder", f) : setFolderId(f.id))} onDoubleClick={() => isAdmin && openFolder(f.id)} data-testid="drive-folder-item">
              <Folder className="h-5 w-5 text-teal-400 shrink-0" />
              <span className="text-sm truncate flex-1">{f.name}</span>
              {isSel("folder", f.id) && <CheckCircle2 className="h-4 w-4 text-teal-300 shrink-0" data-testid="folder-selected-mark" />}
            </div>
          ))}
        </div>
      )}

      {data.files.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {data.files.map((f) => (
            <button key={f.id} onClick={() => (isAdmin ? toggle("file", f) : setPreview(f))} onDoubleClick={() => isAdmin && openFile(f)} className="relative text-left group fade-up select-none" data-testid="drive-asset-card">
              <MediaThumb file={f} className={`aspect-[4/5] rounded-lg transition-shadow ${isSel("file", f.id) ? "ring-4 ring-teal-400" : "group-hover:ring-2 ring-teal-500"}`} />
              {isSel("file", f.id) && <span className="absolute top-2 right-2 h-7 w-7 rounded-full bg-teal-400 text-slate-950 grid place-items-center shadow-lg" data-testid="file-selected-mark"><Check className="h-4 w-4" /></span>}
              <p className="text-xs mt-2 truncate text-slate-300">{f.name}</p>
              <p className="text-[11px] text-muted-foreground font-mono">{f.size < 1048576 ? `${Math.max(1, Math.round(f.size / 1024))} KB` : `${(f.size / 1024 / 1024).toFixed(1)} MB`}</p>
            </button>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground text-sm" data-testid="drive-empty">
          {isAdmin ? "Dateien hierher ziehen oder auf „Hochladen“ klicken." : "In diesem Ordner liegen noch keine Dateien."}
        </div>
      )}
      {isAdmin && data.files.length + data.folders.length > 0 && sel.length === 0 && <p className="text-xs text-slate-500 mt-6" data-testid="drive-select-hint">Tipp: 1× klicken markiert, Doppelklick öffnet.</p>}

      {isAdmin && <ActionBar items={sel} onClear={() => setSel([])} onOpen={() => (sel[0].type === "folder" ? openFolder(sel[0].item.id) : openFile(sel[0].item))}
        onRename={() => setDialog({ kind: "rename" })} onMove={() => setDialog({ kind: "move" })} onCopy={() => setDialog({ kind: "copy" })} onDownload={downloadSel} onDelete={deleteSel} />}
      {dialog?.kind === "rename" && <RenameDialog name={sel[0].item.name} onClose={() => setDialog(null)} onSave={renameSel} />}
      {(dialog?.kind === "move" || dialog?.kind === "copy") && (
        <FolderPicker clientId={clientId} mode={dialog.kind} exclude={sel.filter((s) => s.type === "folder").map((s) => s.item.id)} onClose={() => setDialog(null)} onPick={(t) => (dialog.kind === "move" ? moveSel(t) : copySel(t))} />
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" onClick={() => setPreview(null)} data-testid="file-preview">
          <div className="flex items-center gap-3 px-6 py-4" onClick={(e) => e.stopPropagation()}>
            <p className="text-sm flex-1 truncate">{preview.name}</p>
            <SaveButton file={preview} label="Speichern" className="h-9 px-3 text-sm" testId="download-file-button" />
            {isAdmin && <button onClick={() => delFile(preview.id)} className="text-slate-300 hover:text-rose-400" data-testid="delete-file-button"><Trash2 className="h-5 w-5" /></button>}
            <button onClick={() => setPreview(null)} className="text-slate-300 hover:text-white" data-testid="close-preview-button"><X className="h-5 w-5" /></button>
          </div>
          <div className="flex-1 grid place-items-center p-6 min-h-0" onClick={(e) => e.stopPropagation()}>
            {preview.kind === "video"
              ? <VideoPlayer file={preview} autoPlay className="max-h-full max-w-full" />
              : preview.kind === "text"
                ? <TextPreview file={preview} />
                : <img src={fileUrl(preview.id)} alt={preview.name} className="max-h-full max-w-full object-contain" />}
          </div>
        </div>
      )}
    </div>
  );
}
